import { ArrowLeft, ArrowRight, BriefcaseBusiness, ScanSearch } from "lucide-react";
import { Link } from "wouter";
import { type PillarTopic } from "@/data/contentArchitecture";
import { trackBlogCtaClick } from "@/lib/analytics";
import { resolveRelatedServiceHref } from "@/lib/relatedServices";

type RelatedServicesSectionProps = {
  postSlug: string;
  services: PillarTopic[];
  locale: "en" | "ar";
};

const ARABIC_SERVICE_COPY: Record<string, { name: string; description: string }> = {
  "agentic-ai": {
    name: "استشارات الوكلاء للذكاء الاصطناعي في التسويق والمبيعات",
    description: "صمّم تدفقات عمل ووكلاء بحدود واضحة للمراجعة البشرية والقياس التجاري.",
  },
  "aeo-geo": {
    name: "تحسين الظهور في محركات الإجابة والبحث بالذكاء الاصطناعي",
    description: "نظّم المحتوى والكيانات والمصادر والبيانات المنظمة لأسئلة المشتري المهمة.",
  },
};

/**
 * An editorial, bottom-of-article service list. The same selected services are
 * used in both language editions, while Arabic routes are used only where a
 * published substantive equivalent exists.
 */
export default function RelatedServicesSection({ postSlug, services, locale }: RelatedServicesSectionProps) {
  const visibleServices = services
    .map((service) => ({ service, href: resolveRelatedServiceHref(service, locale) }))
    .filter((selection): selection is { service: PillarTopic; href: string } => Boolean(selection.href));

  const isArabic = locale === "ar";
  const eyebrow = isArabic ? "خدمات ذات صلة" : "Related services";
  const heading = isArabic
    ? "حوّل هذه الرؤية إلى مسار تنفيذ قابل للقياس"
    : "Turn this insight into a measurable service pathway";
  const introduction = isArabic
    ? "اختيرت هذه المسارات وفق موضوعات المقال. استكشف الخدمة الأقرب إلى أولويتك الحالية قبل تحديد نطاق العمل."
    : "These service pathways are selected from the article’s topic clusters. Explore the route closest to your current commercial priority before defining scope.";

  return (
    <section
      className="related-services-section mt-16 border-y border-emerald-500/25 py-8 md:py-10"
      aria-labelledby={`related-services-${postSlug}`}
      data-testid="related-services-section"
      dir={isArabic ? "rtl" : "ltr"}
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
        <BriefcaseBusiness className="h-4 w-4" aria-hidden="true" />
        {eyebrow}
      </div>
      <h2 id={`related-services-${postSlug}`} className="mt-3 max-w-3xl font-serif text-2xl text-foreground md:text-3xl">
        {heading}
      </h2>
      <p className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground">{introduction}</p>

      {visibleServices.length > 0 && <div className="mt-7 grid border-y border-border md:grid-cols-2">
        {visibleServices.map(({ service, href }, index) => {
          const copy = isArabic ? ARABIC_SERVICE_COPY[service.id] : undefined;
          const title = copy?.name ?? service.name;
          const description = copy?.description ?? service.description;
          const ArrowIcon = isArabic ? ArrowLeft : ArrowRight;

          return (
            <Link
              key={service.id}
              href={href}
              className={`group min-h-44 border-border bg-background/35 p-5 hover:bg-emerald-500/[0.06] ${index % 2 === 0 ? "md:border-l" : ""} ${index < visibleServices.length - 1 ? "border-b" : ""} ${index === 0 && visibleServices.length > 1 ? "md:border-b-0" : ""}`}
              onClick={() => trackBlogCtaClick({
                cta_label: isArabic ? `استكشف ${title}` : `Explore ${title}`,
                destination: href,
                post_slug: postSlug,
              })}
            >
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
                {isArabic ? "خدمة" : "Service"}
              </p>
              <h3 className="mt-3 font-serif text-xl text-foreground group-hover:text-emerald-700 dark:group-hover:text-emerald-300">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">{description}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 group-hover:text-emerald-700 dark:text-emerald-200">
                {isArabic ? "استكشف الخدمة" : "Explore service"} <ArrowIcon className="h-4 w-4" aria-hidden="true" />
              </span>
            </Link>
          );
        })}
      </div>}

      <Link
        href="/free-ai-growth-audit/"
        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-emerald-700 dark:text-emerald-200"
        onClick={() => trackBlogCtaClick({
          cta_label: isArabic ? "ابدأ تقييم ظهورك في الذكاء الاصطناعي" : "AI Visibility",
          destination: "/free-ai-growth-audit/",
          post_slug: postSlug,
        })}
      >
        <ScanSearch className="h-4 w-4" aria-hidden="true" />
        {isArabic ? "ابدأ تقييماً مجانياً لظهورك في الذكاء الاصطناعي (بالإنجليزية)" : "Check my AI visibility"}
      </Link>
    </section>
  );
}
